import freshdeskWidgetIcon from "$assets/icons/freshdesk-widget-icon.svg?url";
import { Autocomplete } from "$components/rhf-fields";
import { useUser } from "$global-providers/user-provider";
import SupplyLevelChip from "$routes/_lib/inventory/supply-level-chip";
import { gql, useApolloClient } from "@apollo/client";
import { Box, makeStyles, Typography } from "@material-ui/core";
import React, { useState } from "react";
import { FormattedMessage, useIntl } from "react-intl";
import { useDebouncedCallback } from "use-debounce";
import type { SkuSearchInputQuery } from "./__generated__/sku-search-input.gql";
import { ProductOriginBadge } from "./product-origin-badge";
import { TextField } from "./text-field";

interface StyleProps {
  showNoOptionsText: boolean;
}

const useStyles = makeStyles((theme) => ({
  form: {
    marginTop: theme.spacing(2),
  },
  button: {
    float: "right",
  },
  root: {
    "& $element": {
      color: theme.palette.primary.contrastText,
    },
  },
  link: {
    cursor: "pointer",
    "&:hover": {
      textDecoration: "underline",
    },
  },
  noOptionsText: {
    display: (props: StyleProps) =>
      props.showNoOptionsText ? "block" : "none",
  },
}));

const SKU_SEARCH_QUERY = gql`
  query skuSearchInput($name: String, $facilityId: String!) {
    inventorySearchTmp(
      input: { name: $name, facilityId: $facilityId, active: true }
      options: { pageSize: 50, order: { by: NAME_RELAVANCE } }
    ) {
      edges {
        node {
          id
          name
          supplyLevel
          quantity
          sku {
            id
            externalReferences {
              origin
            }
          }
        }
      }
    }
  }
`;

interface Option {
  id: string;
  name: string;
  quantity?: number | null;
  supplyLevel?: string | null;
  sku?: { externalReferences: ReadonlyArray<{ origin: string }> } | null;
}

export const OptionWithStockLevel: FC<Option> = ({
  name,
  quantity,
  supplyLevel,
  sku,
}) => (
  <Box display="flex" alignItems="center" width="100%" height="44px">
    {name}
    <Box marginLeft={2}>
      <ProductOriginBadge externalReferences={sku?.externalReferences} />
    </Box>
    <Box marginLeft={2}>
      <SupplyLevelChip supplyLevel={supplyLevel} quantity={quantity} />
    </Box>
  </Box>
);

type Props = {
  name: string;
  analyticsEventName: string;
  required?: boolean;
  renderOption?: (option: Option) => React.ReactNode;
} & Partial<React.ComponentProps<typeof Autocomplete>>;

const SkuSearchInput: FC<Props> = ({
  name,
  classes = {},
  required,
  renderOption,
  ...delegateProps
}) => {
  const client = useApolloClient();
  const { facilityId } = useUser();
  const { formatMessage } = useIntl();
  const [searchTerm, setSearchTerm] = useState<string | null>(null);
  const [loading, setLoading] = useState<boolean>(false);
  const [options, setSkusDisplayed] = useState<Option[]>([]);

  const localClasses = useStyles({
    showNoOptionsText: options.length < 1 && !loading && !!searchTerm,
  });

  const callback = useDebouncedCallback(async (newSearchTerm: string) => {
    setSearchTerm(newSearchTerm);
    if (!newSearchTerm?.trim()) {
      return;
    }

    const trimmedSearchTerm = newSearchTerm?.trim();

    setLoading(true);
    const { data } = await client.query<SkuSearchInputQuery>({
      query: SKU_SEARCH_QUERY,
      variables: { name: trimmedSearchTerm, facilityId },
    });
    setLoading(false);

    const skus = data?.inventorySearchTmp.edges.map((edge) => edge.node) || [];
    setSkusDisplayed(skus);
  }, 300);

  return (
    <Autocomplete
      aria-required="false"
      name={name}
      id="sku-search"
      fullWidth
      loading={loading}
      options={options}
      getOptionLabel={(option) => option.name}
      forcePopupIcon={false}
      classes={{ noOptions: localClasses.noOptionsText, ...classes }}
      onInputChange={(_, val) => callback(val)}
      getOptionSelected={(option, value) => option?.id === value?.id}
      noOptionsText={
        <Box display="flex" alignItems="center">
          <Box pr={2}>
            <img
              style={{ height: 30, width: 30 }}
              alt={formatMessage({
                id: "inventory.form.receive.skuSearch.noOptions.icon.alt",
                defaultMessage: "Helpdesk icon",
              })}
              src={freshdeskWidgetIcon}
            />
          </Box>
          <Box>
            <Typography variant="body2">
              <FormattedMessage
                id="inventory.form.receive.skuSearch.noOptions.text"
                defaultMessage="Can’t find the item you’re looking for?"
              />
            </Typography>
            <Typography
              variant="body2"
              color="secondary"
              role="button"
              classes={{ root: localClasses.link }}
              onMouseDown={() => window.fcWidget?.open()}
            >
              <FormattedMessage
                id="inventory.form.receive.skuSearch.noOptions.button.text"
                defaultMessage="Contact us on Helpdesk"
              />
            </Typography>
          </Box>
        </Box>
      }
      renderOption={renderOption}
      renderInput={(errStr) => (params) => (
        <TextField
          {...params}
          label={formatMessage({
            id: "inventory.form.receive.skuSearch.label",
            defaultMessage: "Find Product",
          })}
          variant="outlined"
          data-testid="sku-search"
          InputProps={{
            ...params.InputProps,
            type: "search",
          }}
          error={errStr}
          required={required}
        />
      )}
      {...delegateProps}
    />
  );
};

export default SkuSearchInput;
